// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Where a site physically is, for the fleet map.
//
// ---------------------------------------------------------------------------
// A correction to docs/design/fleet-overview-map.md §1
// ---------------------------------------------------------------------------
// That doc (written in PR 1) states site coordinates are "not captured
// anywhere". That is wrong, and this comment is the correction: the DCPM
// ontology's `SiteTypeAttributes` carries REQUIRED `latitude` and `longitude`
// (see front/src/types/generated/digital-twin.d.ts, generated from
// docs/upstream-specs/data-model-schema.json), and the Digital Twin API
// returns them on the SiteType entity — the vendored example at
// docs/upstream-specs/digital-twin-api.examples.yaml carries a real pair.
//
// So coordinates DO have a contract source. What they don't have is a cheap
// one: reaching them today means `getDatacenterDetails(siteId)` per site —
// a full twin graph fetched for every site on a landing page, to read two
// numbers from each. That is a real backend/caching question (a projection
// endpoint, or coordinates on the site list), which is PR 3's job.
//
// Hence the table below is a FALLBACK, not the intended source of truth. It
// is deliberately small and every entry cites where its value came from; an
// entry is expected to be deleted the moment the same site's coordinate
// arrives from the twin.
// ---------------------------------------------------------------------------

import type { LatLon } from './projection';

export interface SiteCoordinate extends LatLon {
  /** Human-readable place, shown in the marker label. */
  readonly place: string;
  /** Where this value came from — every entry must be traceable. */
  readonly source: string;
}

/**
 * A coordinate the caller already has, supplied per site.
 *
 * Two callers use it: PR 3, once the twin's `latitude`/`longitude` reach the
 * page (see the header), and the `/fleet/demo/:scenario` screens, which
 * carry their own scripted fleets. `place` and `source` are optional so the
 * twin caller can pass the bare pair it has.
 */
export interface ProvidedCoordinate extends LatLon {
  readonly place?: string;
  readonly source?: string;
}

/**
 * site_key → coordinate, for sites whose coordinate we cannot yet read from
 * the twin. Keys are matched case-insensitively (see `lookupCoordinate`)
 * because site keys are not consistently cased across OMS and the twin.
 *
 * Only add an entry backed by a real, citable location. A site missing from
 * this table degrades visibly (the map reports it as not shown) — that is
 * the intended behaviour and is much better than an invented coordinate,
 * which would silently draw a data center in the wrong place.
 */
const FALLBACK_COORDINATES: Readonly<Record<string, SiteCoordinate>> = {
  'ga-10': {
    lat: 33.9526,
    lon: -84.5499,
    place: 'Marietta, GA',
    // Vendored upstream value, not a guess: docs/upstream-specs/
    // weather-api.examples.yaml pins the header weather widget's location as
    // "Prime Edge GA-10, Marietta, GA: lat 33.9526, lon -84.5499", and
    // front/src/utils/constants.ts (WEATHER_LOCATION) uses the same pair.
    source: 'docs/upstream-specs/weather-api.examples.yaml',
  },
  // The mock twin of GA-10, which OMS returns alongside it in local/mock
  // environments. Its own label says "Marietta, GA", so it shares GA-10's
  // location — which is exactly why the map has to handle two data centers
  // at one point rather than drawing one marker on top of another.
  //
  // The UI displays this site as "Prime Edge Data Centers GA-11" / `ga-11`
  // (see config/siteDisplayOverrides.ts). The key here is deliberately the
  // real one: coordinates are looked up by the site's actual `site_key`.
  'mock-ga10': {
    lat: 33.9526,
    lon: -84.5499,
    place: 'Marietta, GA',
    source: "same site as GA-10 (per the site's own label)",
  },
};

/**
 * Resolve a site's coordinate.
 *
 * Precedence, and the reason for it:
 *   1. A coordinate carried on the site itself — the real source once the
 *      twin is wired (see the header); the parameter exists so PR 3 is a
 *      change of caller, not a change of shape. The demo scenarios
 *      (pages/shell-screens/demo/fleetDemoScenarios.ts) already use it.
 *   2. The fallback table above.
 *   3. `undefined` — the site has no known location and the map must say so
 *      rather than place it somewhere plausible.
 */
export function lookupCoordinate(
  siteKey: string | null | undefined,
  provided?: ProvidedCoordinate | null
): SiteCoordinate | undefined {
  if (provided && Number.isFinite(provided.lat) && Number.isFinite(provided.lon)) {
    return {
      lat: provided.lat,
      lon: provided.lon,
      place: provided.place ?? '',
      source: provided.source ?? 'digital-twin',
    };
  }
  if (!siteKey) return undefined;
  return FALLBACK_COORDINATES[siteKey.trim().toLowerCase()];
}

/** Exposed for tests and for the "which sites are mapped" reporting. */
export const FALLBACK_SITE_KEYS = Object.keys(FALLBACK_COORDINATES);
